(() => {
  if (window.EverSilkyUI) return;
  window.EverSilkyUI = true;
  const mobile = window.matchMedia('(max-width: 749px)');
  const compactProduct = window.matchMedia('(max-width: 1199px)');

  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger;
    dialog.showModal();
  }
  document.addEventListener('click', event => {
    const pricingLink = event.target.closest('[data-es-pricing-link]');
    if (pricingLink) {
      const calculator = document.querySelector('[data-es-calc-root]');
      if (calculator) { event.preventDefault(); calculator.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }
    const trigger = event.target.closest('[data-es-dialog-open], [data-calc-reference-open]');
    if (trigger) {
      const root = trigger.closest('[data-es-calc-root]');
      const dialog = trigger.dataset.esDialogOpen
        ? document.getElementById(trigger.dataset.esDialogOpen)
        : root?.querySelector(`[data-calc-reference="${trigger.dataset.calcReferenceOpen}"] dialog`);
      openDialog(dialog, trigger);
    }
    const close = event.target.closest('[data-es-dialog-close]');
    if (close) close.closest('dialog').close();
    if (event.target.matches('dialog.es-dialog')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
  });

  // The current Shopify chat component exposes show(); older Inbox versions
  // use their native launcher. Keep a contact fallback when neither is present.
  function openInbox() {
    const chat = document.querySelector('inbox-online-store-chat, shopify-chat');
    if (chat?.localName === 'shopify-chat' && typeof chat.show === 'function') {
      chat.show();
      return true;
    }
    if (chat?.hasAttribute('open') || chat?.getAttribute('is-open') === 'true') return true;
    const launcherSelector = 'button[part~="activator"], [data-spec="toggle-button"], button.chat-toggle';
    const shell = chat?.shadowRoot;
    // The new Inbox replaces its initial launcher with a nested runtime after
    // the first open. Support both stages so closing and reopening still works.
    const button = shell?.querySelector(launcherSelector)
      || shell?.querySelector('shopify-agent-runtime')?.shadowRoot?.querySelector(launcherSelector);
    if (button) {
      if (button.getAttribute('aria-expanded') !== 'true') button.click();
      return true;
    }
    try {
      const frame = document.getElementById('dummy-chat-button-iframe');
      const launcher = frame?.contentDocument?.getElementById('dummy-chat-button');
      if (launcher) { launcher.click(); return true; }
    } catch (_) { /* The provider may serve the launcher from another origin. */ }
    return false;
  }

  function initCalculator(root) {
    if (root.dataset.esUiReady) return;
    root.dataset.esUiReady = 'true';
    const panel = root.querySelector('[data-calc-panel]');
    const details = root.querySelector('[data-calc-panel-details]');
    const toggle = root.querySelector('[data-calc-panel-toggle]');
    const copy = root.querySelector('[data-calc-copy]');
    const status = root.querySelector('[data-calc-contact-status]');
    const live = root.querySelector('[data-calc-live-selection]');
    let expanded = false;
    const isCompact = () => mobile.matches || (root.classList.contains('es-calc--product') && compactProduct.matches);
    const syncPanel = () => {
      const compact = isCompact();
      root.classList.toggle('is-compact-summary', compact);
      details.hidden = compact && !expanded;
      panel.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute('aria-label', expanded ? 'Hide Order Summary' : 'Show Order Summary');
      toggle.innerHTML = `${expanded ? 'Hide Order Summary' : 'SHOW'} <span aria-hidden="true">${expanded ? '−' : '+'}</span>`;
    };
    toggle.addEventListener('click', () => {
      expanded = !expanded;
      syncPanel();
      if (expanded && isCompact()) panel.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
    mobile.addEventListener('change', syncPanel);
    compactProduct.addEventListener('change', syncPanel);
    root.addEventListener('calc:show-summary', () => {
      // Saving updates the total without opening or moving the mobile panel.
      // Only the customer's Show/Hide action changes its compact disclosure.
      if (isCompact()) return;
      expanded = true;
      syncPanel();
      root.querySelector('[data-calc-order-heading]')?.focus({ preventScroll: true });
      const item = root.querySelector(`[data-calc-list-item-id="${root.calcEngine?.editingItemId}"]`);
      const scrollArea = root.querySelector('.es-calc__panel-scroll');
      if (item && scrollArea) scrollArea.scrollTop += item.getBoundingClientRect().top - scrollArea.getBoundingClientRect().top - 40;
    });
    root.addEventListener('calc:edit-item', () => {
      expanded = false;
      syncPanel();
      const context = root.querySelector('[data-calc-edit-context]');
      const target = context.hidden ? root.querySelector('[data-calc-accordion-toggle][aria-expanded="true"]') : context;
      target?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      target?.focus({ preventScroll: true });
    });
    // Reserve the header's full height. Scrolling must not resize the summary
    // or feed its own changing geometry back into the layout.
    const header = document.querySelector('.section-header:has(sticky-header)');
    const updateHeaderOffset = () => {
      const height = header ? header.getBoundingClientRect().height : 0;
      const offset = header?.classList.contains('shopify-section-header-hidden') ? 0 : height;
      root.style.setProperty('--es-header-height', `${height}px`);
      root.style.setProperty('--es-header-offset', `${offset}px`);
    };
    updateHeaderOffset();
    if (header) {
      new ResizeObserver(updateHeaderOffset).observe(header);
      new MutationObserver(updateHeaderOffset).observe(header, { attributes: true, attributeFilter: ['class'] });
    }
    syncPanel();
    const renderSelection = () => {
      const engine = root.calcEngine;
      if (!engine?.data) return;
      live.replaceChildren();
      const hasOrder = engine.shoppingList.length > 0;
      const editingIndex = engine.shoppingList.findIndex(item => item.id === engine.editingItemId);
      const editing = editingIndex >= 0;
      const draft = root.querySelector('[data-calc-draft]');
      draft.hidden = hasOrder && editing && engine.addedToShoppingList;
      root.querySelector('[data-calc-total-label]').textContent = 'Order Summary';
      root.querySelector('[data-calc-draft-heading]').textContent = editing ? `Changes to item ${editingIndex + 1}` : hasOrder ? 'New item' : 'Item details';
      const editContext = root.querySelector('[data-calc-edit-context]');
      editContext.hidden = !hasOrder;
      root.querySelector('[data-calc-edit-label]').textContent = editing ? `Editing item ${editingIndex + 1}` : 'Adding a new item';
      editContext.querySelector('[data-calc-new-item]').hidden = !editing;
      const fields = ['product_type', 'construction_type', 'color', 'lace', 'frontal_size', 'length', 'density', 'texture', 'cap_type', 'cap_size'];
      const labels = fields.flatMap(field => {
        const value = engine.getStateByField(field);
        return engine.state.productType && value && !engine.groupNotRequiredState[field] ? [engine.getSummaryLabelForField(field)] : [];
      });
      if (engine.lastResult?.valid) labels.push(`Qty ${engine.state.quantity}`);
      live.textContent = labels.length ? labels.join(' · ') : 'Choose your options to get started.';
      const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      const draftPrice = root.querySelector('[data-calc-draft-price]');
      draftPrice.hidden = !hasOrder || !engine.lastResult?.valid;
      draftPrice.textContent = engine.lastResult?.valid ? `Item subtotal: ${money(engine.lastResult.subtotal)}` : '';
      const draftNote = root.querySelector('[data-calc-draft-note]');
      draftNote.hidden = !hasOrder;
      draftNote.textContent = editing ? 'Changes are not included in your order until you update this item.' : 'Not included in your order total until added.';
      root.querySelector('[data-calc-item-estimate]').hidden = !engine.lastResult?.valid;
      root.querySelector('[data-calc-item-price]').textContent = engine.lastResult?.valid ? money(engine.lastResult.subtotal) : '';
      const itemNote = root.querySelector('[data-calc-item-note]');
      itemNote.hidden = !hasOrder || engine.addedToShoppingList;
      itemNote.textContent = editing ? 'Update this item to apply your changes to the order total.' : 'Add this item to include it in your order total.';
      const saveDraft = root.querySelector('[data-calc-save-draft]');
      saveDraft.hidden = !hasOrder || !engine.lastResult?.valid || engine.addedToShoppingList;
      saveDraft.textContent = editing ? 'Update item' : 'Add Item to Order Summary';
      root.querySelector('[data-calc-cancel-edit]').hidden = !editing || engine.addedToShoppingList;
      root.querySelectorAll('[data-calc-list-item-id]').forEach(item => {
        item.classList.toggle('is-editing', item.dataset.calcListItemId === engine.editingItemId);
      });
      copy.disabled = !engine.buildOrderSummary();
    };
    root.addEventListener('calc:updated', renderSelection);
    root.addEventListener('calc:ready', renderSelection);
    renderSelection();
    copy.addEventListener('click', async () => {
      const summary = root.calcEngine?.buildOrderSummary();
      if (!summary) { status.textContent = 'Complete your selections to copy a summary.'; return; }
      try {
        await navigator.clipboard.writeText(summary);
        status.textContent = 'Summary copied. Open Chat with us and paste it into the conversation.';
      } catch (_) {
        // Clipboard permission can be denied, including in embedded previews.
        const dialog = document.createElement('dialog');
        dialog.className = 'es-dialog es-dialog--copy';
        dialog.setAttribute('aria-label', 'Copy your order summary');
        const heading = document.createElement('h2');
        heading.textContent = 'Copy your order summary';
        const hint = document.createElement('p');
        hint.textContent = 'Automatic copying is unavailable. Select and copy the text below, then paste it into chat.';
        const area = document.createElement('textarea');
        area.readOnly = true;
        area.setAttribute('aria-label', 'Order summary');
        area.value = summary;
        const close = document.createElement('button');
        close.type = 'button'; close.textContent = 'Done';
        close.addEventListener('click', () => dialog.close());
        dialog.append(heading, hint, area, close);
        root.append(dialog);
        dialog.addEventListener('close', () => { dialog.remove(); copy.focus(); }, { once: true });
        dialog.showModal(); area.focus(); area.select();
      }
    });
    root.querySelector('[data-calc-chat]').addEventListener('click', async event => {
      // Keep Inbox's outside-click handler from immediately closing the panel
      // opened by this external launcher during the same click event.
      event.stopPropagation();
      const fallback = root.querySelector('[data-calc-contact-fallback]');
      if (openInbox()) { status.textContent = ''; fallback.hidden = true; return; }
      const button = event.currentTarget;
      button.disabled = true;
      status.textContent = 'Opening chat…';
      let opened = false;
      for (let attempt = 0; attempt < 6; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 350));
        if (openInbox()) { opened = true; break; }
      }
      button.disabled = false;
      status.textContent = opened ? '' : 'Chat is unavailable right now. Use the chat icon if visible, or send an inquiry below.';
      fallback.hidden = opened;
    });
  }

  function initGallery(gallery) {
    if (gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const track = gallery.querySelector('[data-es-gallery-track]');
    if (!track) return;
    const slides = [...gallery.querySelectorAll('[data-es-slide]')];
    const buttons = [...gallery.querySelectorAll('[data-es-thumbnail]')];
    let active = 0;
    const setActive = (index, scroll = false) => {
      active = index;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.inert = !mobile.matches && i !== index;
        if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      buttons.forEach((button, i) => {
        button.classList.toggle('is-active', i === index);
        button.setAttribute('aria-pressed', String(i === index));
      });
      if (scroll && mobile.matches) track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    };
    buttons.forEach((button, index) => button.addEventListener('click', () => setActive(index, true)));
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      setActive(Math.max(0, Math.min(slides.length - 1, active + (event.key === 'ArrowRight' ? 1 : -1))), true);
    });
    let pending = false;
    track.addEventListener('scroll', () => {
      if (!mobile.matches || pending) return;
      pending = true;
      requestAnimationFrame(() => {
        const index = slides.reduce((best, slide, i) => Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - slides[0].offsetLeft - track.scrollLeft) ? i : best, 0);
        setActive(index); pending = false;
      });
    }, { passive: true });
    mobile.addEventListener('change', () => setActive(active, true));
    setActive(0);
  }
  function boot() {
    document.querySelectorAll('[data-es-calc-root]').forEach(initCalculator);
    document.querySelectorAll('[data-es-gallery]').forEach(initGallery);
    document.querySelectorAll('dialog.es-dialog').forEach(dialog => {
      if (dialog.dataset.ready) return;
      dialog.dataset.ready = 'true';
      dialog.addEventListener('close', () => {
        dialog.querySelectorAll('video').forEach(video => video.pause());
        dialog._trigger?.focus();
      });
    });
  }
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
